import React, { FC, useEffect, useState } from 'react';
import { FormCard, Title, Description, FieldsContainer, FormButton, FieldRow, Error } from './styled';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import FormBase from 'components/form-base/FormBase';
import { useForm, FormProvider, SubmitHandler, FieldValues, Controller } from 'react-hook-form';
import { SasSubmit } from 'types/sas-submit';
import { TextField, useUtmParams } from 'kl-b2c-ui-kit';
import { DefaultTheme, withTheme } from 'styled-components';
import { EMAIL_PATTERN } from 'consts';
import { getActionsApiService, getApiService, getStorageService } from 'services/api.factory';
import Loading from 'components/common/Loading';
import Success from 'components/common/Success';

const UpdatesForm: FC<{ theme: DefaultTheme }> = ({ theme }) => {
    const { t } = useTranslation(['pages/home', 'pages/action-page', 'validations']);
    const [loading, setLoading] = useState<boolean>(false);
    const storageService = getStorageService();
    const apiService = getApiService(storageService);
    const actionsApiService = getActionsApiService(apiService);
    const [success, setSuccess] = useState(false);
    const [doi, setDoi] = useState(false);

    const methods = useForm<SasSubmit>();

    const {
        control,
        formState: { errors },
    } = methods;

    const onSubmit: SubmitHandler<FieldValues> = async (data) => {
        setLoading(true);
        const utm = useUtmParams();

        const requestData: SasSubmit = {
            email: data.email,
            captchaCode: data.captchaCode,
            sendMessage: false,
            agreement: {
                marketingIsSelected: true,
                marketingCheckBoxText: t('pages/home:agreements'),
            },
            ...utm,
        };

        const res = await actionsApiService.addApplication(requestData);
        if (res.status == 200) {
            setLoading(false);
            setSuccess(true);
        } else if (res.status == 202) {
            setLoading(false);
            setSuccess(true);
            setDoi(true);
        }
    };

    useEffect(() => {
        if (success || loading) {
            document.body.classList.add('is-modal-open');
        } else {
            document.body.classList.remove('is-modal-open');
        }
    }, [success, loading]);

    return (
        <>
            <FormCard>
                <Title variant={'h3'} fontWeight={'regular'} color={'textSecondary'}>
                    {t('pages/home:updates')}
                </Title>
                <Description variant={'p2'} fontWeight={'light'} color={'textSecondary'}>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(t('pages/home:updates-desc')),
                        }}
                    />
                </Description>
                <FormProvider {...methods}>
                    <FormBase
                        type={'secondary'}
                        gap={'24px'}
                        policies={[
                            {
                                name: 'agreement.marketingCheckBoxText',
                                label: t('pages/home:agreements'),
                                required: true,
                            },
                        ]}
                        submit={onSubmit}
                        hideSubmitBtn
                        loading={loading}
                    >
                        <FieldsContainer>
                            <Controller
                                name={'email'}
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('validations:fieldIsRequired'),
                                    },
                                    maxLength: {
                                        value: 255,
                                        message: t('validations:fieldIsTooLong', {
                                            length: 255,
                                        }),
                                    },
                                    pattern: {
                                        value: EMAIL_PATTERN,
                                        message: t('validations:emailHasNotCorrectFormat'),
                                    },
                                }}
                                render={({ field: { onChange } }) => (
                                    <FieldRow>
                                        <TextField
                                            type={'text'}
                                            variant={'border'}
                                            transparent
                                            color={theme.palette.text.secondary}
                                            placeholder={t('pages/home:email-placeholder')}
                                            onChange={(value) => onChange(value)}
                                            error={!!errors['email']}
                                        />
                                        {errors?.email && (
                                            <Error variant="p5">
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            errors?.email?.message ??
                                                            DOMPurify.sanitize(t('validations:default')),
                                                    }}
                                                />
                                            </Error>
                                        )}
                                    </FieldRow>
                                )}
                            />
                            <FormButton color={'primary-contrast'} type={'submit'}>
                                {t('pages/home:subscribe')}
                            </FormButton>
                        </FieldsContainer>
                    </FormBase>
                </FormProvider>
            </FormCard>
            {loading && <Loading label={t('pages/home:data-sending')} />}
            {success && (
                <Success additionalText={doi ? t('pages/action-page:doi-text') : ''} close={() => setSuccess(false)} />
            )}
        </>
    );
};

export default withTheme(UpdatesForm);
